import { useState } from 'react';
import { ActionIcon, Badge, Button, Group, Paper, ScrollArea, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconDialpad,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhoneOff,
  IconPlayerPause,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useSoftphone } from '@/hooks/use-softphone';
import { formatClock, formatPhone } from '../format';
import { useNow } from '../use-now';
import { DIALPAD_KEYS } from './agent-status';
import type { ActiveCall } from './types';

function CallTimer({ startedAt }: { startedAt: number }) {
  const now = useNow();

  return <>{formatClock(Math.floor((now - startedAt) / 1000))}</>;
}

function callBackgroundColor(activeCall: ActiveCall) {
  if (activeCall.isHeld) return 'var(--mantine-color-yellow-light)';

  return activeCall.state === 'ringing' ? 'var(--mantine-color-blue-light)' : 'var(--mantine-color-green-light)';
}

export function ActiveCallView({ activeCall }: { activeCall: ActiveCall }) {
  const { toggleMute, toggleHold, hangupCall, sendDtmf } = useSoftphone();
  const [showDtmf, setShowDtmf] = useState(false);

  const sessionVars = Object.entries(activeCall.sessionVariables ?? {}).filter(([key]) => !key.startsWith('_'));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div
        className="px-4 py-3 border-b flex items-center justify-between shrink-0"
        style={{
          borderColor: 'var(--mantine-color-default-border)',
          backgroundColor: callBackgroundColor(activeCall),
        }}
      >
        <Group gap="md">
          <div>
            <Text fw={600} size="lg" ff="monospace">
              {activeCall.remoteCallerNumber
                ? formatPhone(activeCall.remoteCallerNumber)
                : (activeCall.remoteCallerName ?? 'Call')}
            </Text>
            {activeCall.remoteCallerName && activeCall.remoteCallerNumber && (
              <Text size="sm" c="dimmed">
                {activeCall.remoteCallerName}
              </Text>
            )}
          </div>
          {activeCall.state === 'ringing' ? (
            <Badge color="blue" variant="light" size="lg">
              Dialing...
            </Badge>
          ) : (
            <Badge color={activeCall.isHeld ? 'yellow' : 'green'} variant="light" size="lg" ff="monospace">
              {activeCall.isHeld ? 'On Hold' : <CallTimer startedAt={activeCall.startedAt} />}
            </Badge>
          )}
        </Group>

        <Group gap="sm">
          <Tooltip label={activeCall.isMuted ? 'Unmute' : 'Mute'}>
            <ActionIcon
              size="xl"
              radius="xl"
              variant={activeCall.isMuted ? 'filled' : 'light'}
              color={activeCall.isMuted ? 'red' : 'gray'}
              onClick={toggleMute}
            >
              {activeCall.isMuted ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label={activeCall.isHeld ? 'Resume' : 'Hold'}>
            <ActionIcon
              size="xl"
              radius="xl"
              variant={activeCall.isHeld ? 'filled' : 'light'}
              color={activeCall.isHeld ? 'yellow' : 'gray'}
              onClick={toggleHold}
            >
              {activeCall.isHeld ? <IconPlayerPlay size={20} /> : <IconPlayerPause size={20} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Keypad">
            <ActionIcon
              size="xl"
              radius="xl"
              variant={showDtmf ? 'filled' : 'light'}
              color="gray"
              onClick={() => setShowDtmf((v) => !v)}
            >
              <IconDialpad size={20} />
            </ActionIcon>
          </Tooltip>
          <ActionIcon size="xl" radius="xl" color="red" variant="filled" onClick={hangupCall}>
            <IconPhoneOff size={20} />
          </ActionIcon>
        </Group>
      </div>

      {showDtmf && (
        <div
          className="px-4 py-3 border-b flex justify-center shrink-0"
          style={{ borderColor: 'var(--mantine-color-default-border)' }}
        >
          <SimpleGrid cols={3} spacing={4} w={200}>
            {DIALPAD_KEYS.map((key) => (
              <Button
                key={key}
                variant="default"
                size="compact-md"
                onClick={() => sendDtmf(key)}
                fw={600}
                styles={{ label: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
              >
                {key}
              </Button>
            ))}
          </SimpleGrid>
        </div>
      )}

      <ScrollArea className="flex-1" p="md">
        <Stack gap="md">
          {sessionVars.length === 0 ? (
            <Paper p="lg">
              <Text c="dimmed" ta="center">
                No caller information available
              </Text>
            </Paper>
          ) : (
            <Paper p="md">
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm" style={{ letterSpacing: '0.05em' }}>
                Caller Information
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {sessionVars.map(([key, value]) => (
                  <div key={key}>
                    <Text size="xs" c="dimmed" tt="capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </Text>
                    <Text size="sm" fw={500}>
                      {value}
                    </Text>
                  </div>
                ))}
              </SimpleGrid>
            </Paper>
          )}
        </Stack>
      </ScrollArea>
    </div>
  );
}
